/**
 * MissionPlanner Component
 *
 * Main mission planning container
 * Integrates waypoint list, toolbar, stats, and Cesium map
 */

import { useEffect, useState } from 'react';
import type { Viewer as CesiumViewer } from 'cesium';
import { WaypointList as _WaypointList } from './WaypointList';
import { MissionStats } from './MissionStats';
import { WaypointEditor } from './WaypointEditor';
import { useMission } from '../hooks/useMission';
import { useWaypoints } from '../hooks/useWaypoints';
import { MISSION_DEFAULTS } from '@/constants/mission-defaults';
import {
  addWaypointMarker,
  enableAddWaypointMode,
  enableWaypointDragging,
  removeWaypointMarker,
  renderFlightPath,
} from '../utils/cesium-waypoint-renderer';
import type { Position } from '../types/mission.types';
import { cn } from '@/lib/utils';

interface MissionPlannerProps {
  viewer?: CesiumViewer;
  className?: string;
  isListVisible?: boolean;
  setIsListVisible?: (visible: boolean) => void;
}

export function MissionPlanner({ viewer, className, isListVisible: externalIsListVisible, setIsListVisible: externalSetIsListVisible }: MissionPlannerProps) {
  const {
    mission,
    isAddingWaypoint,
    addWaypoint,
    createMission,
    setAddingWaypointMode,
  } = useMission();
  const { waypoints, selectedWaypoint, updateWaypoint, selectWaypoint } =
    useWaypoints();
  const [internalIsListVisible, setInternalIsListVisible] = useState(true);
  const isListVisible = externalIsListVisible !== undefined ? externalIsListVisible : internalIsListVisible;
  const setIsListVisible = externalSetIsListVisible || setInternalIsListVisible;
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Open editor when waypoint is selected
  useEffect(() => {
    if (selectedWaypoint) {
      setIsEditorOpen(true);
    }
  }, [selectedWaypoint]);

  // Close editor and deselect waypoint when editor is closed
  const handleEditorClose = () => {
    setIsEditorOpen(false);
    selectWaypoint(null);
  };

  // Create default mission if none exists
  useEffect(() => {
    if (!mission) {
      createMission('New Mission');
    }
  }, [mission, createMission]);

  // Render waypoint markers on map
  useEffect(() => {
    if (!viewer) return;

    // Clear existing markers
    waypoints.forEach((wp) => {
      removeWaypointMarker(viewer, wp.id);
    });

    // Add current waypoints
    waypoints.forEach((waypoint) => {
      const isSelected = selectedWaypoint?.id === waypoint.id;
      addWaypointMarker(viewer, waypoint, isSelected, false);
    });

    // Render flight path
    renderFlightPath(viewer, waypoints);
  }, [viewer, waypoints, selectedWaypoint]);

  // Handle click-to-add waypoint mode
  useEffect(() => {
    if (!viewer || !isAddingWaypoint) return;

    const cleanup = enableAddWaypointMode(viewer, (position: Position) => {
      addWaypoint({
        latitude: position.latitude,
        longitude: position.longitude,
        altitude: MISSION_DEFAULTS.defaultAltitude,
      });
    });

    return cleanup;
  }, [viewer, isAddingWaypoint, addWaypoint]);

  // Handle waypoint dragging on map
  useEffect(() => {
    if (!viewer) return;

    const cleanup = enableWaypointDragging(
      viewer,
      (waypointId: string, position: Position) => {
        updateWaypoint(waypointId, {
          latitude: position.latitude,
          longitude: position.longitude,
          altitude: position.altitude,
        });
      }
    );

    return cleanup;
  }, [viewer, updateWaypoint]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to cancel add mode
      if (e.key === 'Escape' && isAddingWaypoint) {
        setAddingWaypointMode(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAddingWaypoint, setAddingWaypointMode]);

  if (!mission) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute inset-0 pointer-events-none flex flex-col',
        className
      )}
    >
      {/* Top section containing map and sidebars */}
      <div className="flex-1 relative">
        {/* Waypoint List (left sidebar) */}
        {/* {isListVisible && (
          <div className="absolute left-0 top-0 bottom-0 w-80 pointer-events-auto">
            <div className="h-full flex flex-col bg-slate-900 border-r border-slate-700 shadow-xl">
              <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-white">
                    Waypoints
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {waypoints.length} waypoint
                    {waypoints.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => setIsListVisible(false)}
                  className="text-slate-400 hover:text-white transition-colors p-1"
                  title="Hide Waypoints"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <_WaypointList viewer={viewer} />
              </div>
            </div>
          </div>
        )} */}

        {/* Show Waypoints Button (when hidden) */}
        {!isListVisible && (
          <button
            onClick={() => setIsListVisible(true)}
            className="absolute left-4 top-36 pointer-events-auto bg-slate-900 text-white px-3 py-2 rounded-md shadow-lg hover:bg-slate-800 transition-colors text-sm z-10"
          >
            → Show Waypoints
          </button>
        )}

        {/* Waypoint Editor (right sidebar) */}
        <WaypointEditor isOpen={isEditorOpen} onClose={handleEditorClose} />
      </div>

      {/* Stats Panel (bottom) */}
      <div>
        <MissionStats />
      </div>
    </div>
  );
}
